/**
 * QuoteQueueWidget (#2652) — open quotations that need a hand: accepted ones
 * to schedule, ones about to expire, unsent ones and ones the customer has
 * not answered. Same buckets as the quote queue page; a click opens the work
 * order, the header opens the Quotes tab of the work orders page (#3788).
 */
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Link,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import { formatDistanceToNowStrict } from 'date-fns';
import type { QuoteQueueSummary } from '@/hooks/useDashboard';
import type { QuoteQueueBucket } from '@/hooks/useQuoteQueue';
import { useOpenWorkOrder, useViewedWorkOrderId } from '@/hooks/useWorkOrderWindow';
import { dateFnsLocale } from '@/utils/locale';
import { handlePlainLeftClick } from './dashboardLinkClick';

interface QuoteQueueWidgetProps {
  data?: QuoteQueueSummary | null;
}

/** Counts shown as chips, in the order a salesperson would act on them. */
const COUNT_BUCKETS: readonly QuoteQueueBucket[] = ['accepted', 'expiring', 'unsent', 'awaiting', 'expired'];

const CHIP_COLOR: Record<QuoteQueueBucket, 'success' | 'warning' | 'info' | 'primary' | 'error' | 'default'> = {
  accepted: 'success',
  expiring: 'warning',
  unsent: 'info',
  awaiting: 'primary',
  expired: 'error',
  rejected: 'default',
};

export function QuoteQueueWidget({ data }: QuoteQueueWidgetProps) {
  const { t } = useTranslation('dashboard');
  const { t: tw } = useTranslation('workorders');
  const openWorkOrder = useOpenWorkOrder();
  const viewedWorkOrderId = useViewedWorkOrderId();
  const dateLocale = dateFnsLocale();

  const items = data?.items ?? [];
  const counts = data?.counts;

  const age = (item: QuoteQueueSummary['items'][number]): string => {
    const since = item.latestSend?.sentAt ?? item.createdAt;
    return formatDistanceToNowStrict(new Date(since), { locale: dateLocale });
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <RequestQuoteIcon color="secondary" />
          <Typography variant="h6">{t('quoteQueue.title')}</Typography>
          {counts && counts.open > 0 && (
            <Chip label={counts.open} size="small" color="secondary" sx={{ ml: 'auto' }} />
          )}
        </Box>

        {counts && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
            {COUNT_BUCKETS.filter((bucket) => counts[bucket] > 0).map((bucket) => (
              <Chip
                key={bucket}
                component={RouterLink}
                to={`/workorders?tab=quotes&bucket=${bucket}`}
                clickable
                label={`${tw(`quoteQueue.buckets.${bucket}`)} ${counts[bucket]}`}
                size="small"
                color={CHIP_COLOR[bucket]}
                variant="outlined"
                sx={{ fontSize: '0.7rem', textDecoration: 'none' }}
              />
            ))}
          </Box>
        )}

        {items.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('quoteQueue.empty')}
          </Typography>
        ) : (
          <List disablePadding>
            {items.map((item) => (
              <ListItemButton
                key={item.id}
                component={RouterLink}
                to={`/workorders/${item.id}`}
                selected={item.id === viewedWorkOrderId}
                onClick={(event) => {
                  handlePlainLeftClick(event, () => openWorkOrder(item.id));
                }}
                sx={{ borderRadius: 1, mb: 0.5, px: 1, gap: 1, textDecoration: 'none', color: 'inherit' }}
              >
                <Chip
                  label={tw(`quoteQueue.buckets.${item.bucket}`)}
                  size="small"
                  color={CHIP_COLOR[item.bucket]}
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                />
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {item.vehicleRegNumber || item.customerName}
                      </Typography>
                      {item.vehicleRegNumber && (
                        <Typography variant="body2" color="text.secondary">
                          {item.customerName}
                        </Typography>
                      )}
                    </Box>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {item.latestSend
                        ? t('quoteQueue.sentAgo', { age: age(item) })
                        : t('quoteQueue.createdAgo', { age: age(item) })}
                      {item.serviceNames ? ` · ${item.serviceNames}` : ''}
                    </Typography>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        )}

        <Box sx={{ mt: 1 }}>
          <Link
            component={RouterLink}
            to="/workorders?tab=quotes"
            variant="body2"
            underline="none"
          >
            {t('quoteQueue.openAll')}
          </Link>
        </Box>
      </CardContent>
    </Card>
  );
}
